import { TypedDocumentNode, gql } from '@apollo/client';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { isTypeFn } from '@sorare/core/src/lib/gql';

import { BaseObjectiveBar } from './BaseObjectiveBar';
import type { ObjectiveBar_managerTask } from './__generated__/index.graphql';
import { ObjectiveBar_so5Leaderboard } from './__generated__/index.graphql';

type Props = {
  so5Leaderboard: ObjectiveBar_so5Leaderboard;
  tasks: ObjectiveBar_managerTask[];
  displayTaskInState?: ManagerProgressionTaskState;
};

export const ObjectiveBar = ({
  so5Leaderboard,
  tasks,
  displayTaskInState = ManagerProgressionTaskState.READY,
}: Props) => {
  const managerProgressionTasks = tasks.filter(
    isTypeFn('ManagerProgressionTask')
  );

  const nextTask = managerProgressionTasks.find(
    task => task.aasmState === displayTaskInState
  );

  if (!nextTask) {
    return null;
  }

  const bundleRewards = nextTask.configRewards.filter(
    isTypeFn('ProbabilisticBundleConfigReward')
  );
  const bundleConfig = bundleRewards.at(0)?.probabilisticBundleConfig;

  return (
    <BaseObjectiveBar
      key={nextTask.id}
      so5Leaderboard={so5Leaderboard}
      probabilisticBundleConfig={bundleConfig}
      completedOrClaimed={
        nextTask.aasmState === ManagerProgressionTaskState.COMPLETED ||
        nextTask.aasmState === ManagerProgressionTaskState.CLAIMED
      }
      description={nextTask.description}
      rewardCount={bundleRewards.length}
    />
  );
};

ObjectiveBar.fragments = {
  so5Leaderboard: gql`
    fragment ObjectiveBar_so5Leaderboard on So5Leaderboard {
      slug
      ...BaseObjectiveBar_so5Leaderboard
    }
  ` as TypedDocumentNode<ObjectiveBar_so5Leaderboard>,
  managerTask: gql`
    fragment ObjectiveBar_managerTask on ManagerTask {
      ... on ManagerProgressionMission {
        id
      }
      ... on ManagerProgressionTask {
        id
        aasmState
        description
        configRewards {
          __typename
          ... on ProbabilisticBundleConfigReward {
            probabilisticBundleConfig {
              id
              ...BaseObjectiveBar_probabilisticBundleConfig
            }
          }
        }
      }
    }
    ${BaseObjectiveBar.fragments.so5Leaderboard}
    ${BaseObjectiveBar.fragments.probabilisticBundleConfig}
  ` as TypedDocumentNode<ObjectiveBar_managerTask>,
};
